import store from 'store';
import {
  LAST_REQUEST_TIME_SESSION_STORAGE_KEY,
  MAIN_STORAGE_KEY,
  MANUAL_SESSION_SAVE_SESSION_KEY,
} from './storageConstants';

/** Type representing stored data as a record of string keys to unknown values */
type StoreData = Record<string, unknown>;

/** Function type for storing manager data */
export type ManagerStoreFn = () => StoreData;

/**
 * Manages persistent storage operations.
 * Handles storing, loading, and managing data in browser storage.
 */
export class StorageManager {
  private static storeSubscriptions: Record<string, ManagerStoreFn> = {};
  private static loadedStoreCache: Record<string, StoreData> = {};
  private static persist = true;

  public static purgeModuleStates() {
    this.removeItem(MAIN_STORAGE_KEY);
  }

  /**
   * Registers a storage function for a namespace.
   * @param namespace - The namespace to store data under
   * @param storeFn - Function that returns data to be stored
   */
  public static store(namespace: string, storeFn: ManagerStoreFn) {
    if (namespace in this.storeSubscriptions) return;
    this.storeSubscriptions[namespace] = storeFn;
  }

  /**
   * Loads and retrieves data for a namespace from the cache.
   * Once retrieved, the data is removed from the cache.
   * @param namespace - The namespace to load data from
   * @returns The stored data or null if not found
   */
  public static load(namespace: string) {
    if (!(namespace in this.loadedStoreCache)) return null;

    const data = this.loadedStoreCache[namespace];

    delete this.loadedStoreCache[namespace];

    return data;
  }

  /**
   * Loads all stored data into the cache.
   */
  public static doLoad() {
    const stored = store.get(MAIN_STORAGE_KEY);
    if (!stored) return;

    this.loadedStoreCache = stored;
  }

  /**
   * Stores all registered namespace data to persistent storage.
   * Calls each registered store function and saves the results.
   */
  public static doStore() {
    if (!this.persist) return;
    const storage: Record<string, StoreData> = {};

    for (const [namespace, storeFn] of Object.entries(
      this.storeSubscriptions,
    )) {
      storage[namespace] = storeFn();
    }

    try {
      store.set(MAIN_STORAGE_KEY, storage);
    } catch (err) {
      //memory overflow, or no access to storage
    }
  }

  public static disablePersist() {
    this.persist = false;
    store.remove(MAIN_STORAGE_KEY);
  }

  /**
   * Purges all stored data.
   * Removes both main storage and manual session save data.
   */
  public static purge() {
    store.remove(MAIN_STORAGE_KEY);
    store.remove(MANUAL_SESSION_SAVE_SESSION_KEY);
    window.sessionStorage.removeItem(LAST_REQUEST_TIME_SESSION_STORAGE_KEY);
  }

  /**
   * Sets a value in storage for a specific key.
   * @param key - The key to store the value under
   * @param value - The value to store
   */
  public static setItem(key: string, value: unknown) {
    store.set(key, value);
  }

  /**
   * Retrieves a value from storage by key.
   * @param key - The key to retrieve the value for
   * @returns The stored value or undefined if not found
   */
  public static getItem(key: string) {
    return store.get(key);
  }

  /**
   * Removes an item from storage by key.
   * @param key - The key of the item to remove
   */
  public static removeItem(key: string) {
    store.remove(key);
  }
}
